import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environment";
import { Observable, of } from "rxjs";
import { ListResponse } from "../interfaces/list-response.interface";
import { sanitizeParams } from "../sanitize-params";
import { CoPhysician } from "src/app/features/facility/physicians/physicians.interface";

export interface Physician {
  id: 0;
  npi: string;
  firstname: string;
  lastname: string;
  email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  primaryphone: string;
  altphone: string;
  fax: string;
  physiciantypeid: 0;
  physiciangroup: string;
  physicianspecialtyid: number;
  taxid: string;
  taxonomyid: number;
  license: string;
  status: string;
  modifiedby: string;
  modifiedtime: string;
  accountexpirationdate: Date;
}

export interface PhysicianListItem {
  id: number;
  name: string;
  address: string;
  primaryphone: string;
  fax: string;
  status: string;
}

export interface PhysicianSearchOptions {
  facilityId: string;
  firstName?: string;
  lastName?: string;
  npi?: string;
  state?: any[];
}

export interface PhysicianSearchResult {
  npi: string;
  firstname: string;
  lastname: string;
}
export interface CoPhysicians {
  physicianId: number;
  isCophysician: boolean;
}

@Injectable({ providedIn: "root" })
export class PhysicianClient {
  constructor(private httpClient: HttpClient) {}

  getPhysiciansByFacilityId(
    facilityId: string,
    includeDisabled: boolean = false
  ): Observable<ListResponse<PhysicianListItem>> {
    return this.httpClient.get<ListResponse<PhysicianListItem>>(
      `${environment.endpoint}/physician/list`,
      { params: sanitizeParams({ facilityid: facilityId, includeDisabled }) }
    );
  }

  getPhysician(facilityId: string, physicianId: number): Observable<Physician> {
    return this.httpClient.get<Physician>(
      `${environment.endpoint}/physician/get/${facilityId}/${physicianId}`
    );
  }

  createPhysician(physician: any): Observable<Physician> {
    return this.httpClient.post<Physician>(
      `${environment.endpoint}/physician/add`,
      sanitizeParams(physician)
    );
  }

  editPhysician(physician: any): Observable<Physician> {
    return this.httpClient.put<Physician>(
      `${environment.endpoint}/physician/edit`,
      sanitizeParams(physician)
    );
  }

  enablePhysician(facilityId: string, physicianId: number): Observable<void> {
    return this.httpClient.put<void>(
      `${environment.endpoint}/physician/enabled/${facilityId}/${physicianId}`,
      {}
    );
  }

  disablePhysician(facilityId: string, physicianId: number): Observable<void> {
    return this.httpClient.put<void>(
      `${environment.endpoint}/physician/disabled/${facilityId}/${physicianId}`,
      {}
    );
  }

  deletePhysician(facilityId: string, physicianId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${environment.endpoint}/physician/delete/${facilityId}/${physicianId}`
    );
  }

  findPhysicians(
    options: PhysicianSearchOptions
  ): Observable<ListResponse<PhysicianSearchResult>> {
    const params: HttpParams = new HttpParams({
      fromObject: sanitizeParams(options),
    });
    return this.httpClient.get<ListResponse<PhysicianSearchResult>>(
      `${environment.endpoint}/physician/cms-list`,
      { params }
    );
  }

  getPhysicianDetails(facilityId: string, npi: string): Observable<Physician> {
    return this.httpClient.get<Physician>(
      `${environment.endpoint}/physician/physician-details/${facilityId}/${npi}`
    );
  }

  getCoPhysicians(
    facilityid: string,
    physicianid: string
  ): Observable<CoPhysician[]> {
    return this.httpClient.get<CoPhysician[]>(
      `${environment.endpoint}/physician/GetCoPhysicians/${facilityid}/${physicianid}`
    );
  }

  addCoPhysician(
    facilityid: string,
    physicianid: number,
    coPhysicians: CoPhysicians[]
  ) {
    return this.httpClient.post(
      `${environment.endpoint}/physician/AddOrRemoveCoPhysicians`,
      { facilityid, physicianid, coPhysicians }
    );
  }
}
